import { UserRetailAccount } from '@shared/types';
import { RelativeTime } from '@shared/RelativeTime';
import { RefreshIcon } from '@shared/RefreshIcon';
import { FileText, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface PortfolioActionsBarProps {
  account: UserRetailAccount;
  allAccounts: UserRetailAccount[];
  accountTitle: string;
  contentLoading: boolean;
  lastUpdatedAt?: Date | null;
  updating: boolean;
  rebalancing: boolean;
  isUnfunded?: boolean;
  onSelectAccount: (id: string) => void;
  onRefresh: () => void;
  onRebalance: () => void;
  onToggleActive: () => void;
}

const accountLabel = (acc: UserRetailAccount) =>
  acc.nickname || (acc.isPaper ? 'Paper Trading' : 'Live Trading');

const AccountIcon = ({
  isPaper,
  className,
}: {
  isPaper?: boolean | null;
  className?: string;
}) =>
  isPaper ? (
    <FileText className={className} aria-hidden />
  ) : (
    <Banknote className={className} aria-hidden />
  );

// Mode pill (Paper/Live) with an active-status dot — shared by the switcher
// trigger, the menu rows, and the single-account display so all three match.
const AccountModeBadge = ({
  acc,
  className,
}: {
  acc: UserRetailAccount;
  className?: string;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.04em]',
      acc.isPaper
        ? 'border-primary/35 bg-primary/12 text-primary'
        : 'border-secondary/35 bg-secondary/12 text-secondary',
      className,
    )}
    title={acc.active ? 'Account is active' : 'Account is inactive'}
    aria-label={`${acc.isPaper ? 'Paper' : 'Live'} account, ${
      acc.active ? 'active' : 'inactive'
    }`}
  >
    <span
      className={cn(
        'size-2 shrink-0 rounded-full',
        acc.isPaper
          ? 'bg-[color:var(--nq-blue-bright)]'
          : 'bg-[color:var(--nq-gold-bright)]',
        acc.active
          ? acc.isPaper
            ? 'shadow-[0_0_0_3px_rgba(37,180,224,0.28)]'
            : 'shadow-[0_0_0_3px_rgba(247,148,29,0.28)]'
          : 'opacity-40',
      )}
      aria-hidden
    />
    {acc.isPaper ? 'Paper' : 'Live'}
  </span>
);

// Icon + display-font account name, tinted blue (paper) or gold (live).
const AccountTitle = ({
  acc,
  title,
}: {
  acc: UserRetailAccount;
  title: string;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-2 font-display text-xl font-bold leading-none capitalize',
      acc.isPaper ? 'text-primary' : 'text-secondary',
    )}
  >
    <AccountIcon
      isPaper={acc.isPaper}
      className="block size-5 shrink-0 translate-y-[2px]"
    />
    {title}
  </span>
);

const SignalForgeIcon = ({ active }: { active: boolean }) => (
  <svg
    className={
      active
        ? 'text-[color:var(--nq-gold-bright)] [filter:drop-shadow(0_0_4px_rgba(247,148,29,0.45))]'
        : 'opacity-55'
    }
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 18V14" />
    <path d="M8 18V10" />
    <path d="M12 18V12" />
    <path d="M16 18V6" />
    <path d="M16 2v2.5" />
    <path d="M14.5 3.5h3" />
    <path d="M18.5 3.5l-1.5 1.5" />
    <path d="M13.5 3.5l1.5 1.5" />
  </svg>
);

const PortfolioActionsBar = ({
  account,
  allAccounts,
  accountTitle,
  contentLoading,
  lastUpdatedAt,
  updating: _updating,
  rebalancing: _rebalancing,
  isUnfunded = false,
  onSelectAccount,
  onRefresh,
  onRebalance: _onRebalance,
  onToggleActive: _onToggleActive,
}: PortfolioActionsBarProps) => {
  const fundFirstTitle = isUnfunded ? 'Fund your account first' : undefined;
  const sfOn = account.signal_forge_enabled === true;

  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {allAccounts.length > 1 ? (
          <Select value={account.id} onValueChange={onSelectAccount}>
            <SelectTrigger
              aria-label="Switch account"
              className="h-auto w-auto gap-2 rounded-lg border-0 bg-transparent px-2 py-1 transition-colors hover:bg-muted/50 [&>span]:line-clamp-none"
            >
              <AccountTitle acc={account} title={accountTitle} />
              <AccountModeBadge acc={account} />
            </SelectTrigger>
            <SelectContent
              align="start"
              className="min-w-[16rem] bg-[color:var(--nq-surface)]"
            >
              {allAccounts.map((acc) => (
                <SelectItem key={acc.id} value={acc.id} className="py-2">
                  <span className="flex items-center gap-2.5">
                    <AccountIcon
                      isPaper={acc.isPaper}
                      className={cn(
                        'block size-4 shrink-0 translate-y-px',
                        acc.isPaper ? 'text-primary' : 'text-secondary',
                      )}
                    />
                    <span className="font-medium">{accountLabel(acc)}</span>
                    <AccountModeBadge acc={acc} className="ml-1" />
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="mr-1 inline-flex items-center gap-2">
            <AccountTitle acc={account} title={accountTitle} />
            <AccountModeBadge acc={account} />
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onRefresh}
          disabled={contentLoading}
          title="Refresh portfolio data"
          aria-label={contentLoading ? 'Refreshing…' : 'Refresh portfolio data'}
          className="h-auto items-center gap-2 px-4 py-2 text-left text-[13px] font-semibold normal-case"
        >
          <RefreshIcon
            spinning={contentLoading}
            className={`shrink-0${contentLoading ? ' animate-spin' : ''}`}
          />
          <span className="flex min-w-0 flex-col items-start gap-px">
            <span className="text-[13px] font-semibold leading-tight">
              {contentLoading ? 'Refreshing…' : 'Refresh'}
            </span>
            {lastUpdatedAt && !contentLoading && (
              <span
                className="whitespace-nowrap text-[10px] font-medium leading-tight opacity-75"
                aria-live="polite"
              >
                Updated <RelativeTime value={lastUpdatedAt} />
              </span>
            )}
          </span>
        </Button>
        <div
          className={`relative inline-flex items-center gap-2 rounded-lg border px-3 py-2 pl-3 text-left text-[13px] font-semibold transition-[background,border-color] ${
            sfOn
              ? 'border-secondary/45 bg-[image:linear-gradient(135deg,rgba(247,148,29,0.22)_0%,rgba(255,193,7,0.12)_100%)] text-[color:var(--nq-gold-amber)] shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_0_0_1px_rgba(247,148,29,0.08),0_4px_16px_rgba(247,148,29,0.12)]'
              : 'border-[color:rgba(255,255,255,0.1)] bg-[color:rgba(255,255,255,0.04)] text-muted-foreground'
          }${isUnfunded ? ' pointer-events-none cursor-not-allowed opacity-45' : ''}`}
          title={
            fundFirstTitle ??
            (sfOn
              ? 'SignalForge is active — click positions to explore signal graphs.'
              : 'SignalForge is disabled')
          }
        >
          <SignalForgeIcon active={sfOn} />
          <span className="flex min-w-0 flex-col items-start gap-px">
            <span className="text-[13px] font-semibold leading-tight">SignalForge</span>
            <span className="whitespace-nowrap text-[10px] font-medium leading-tight tracking-[0.02em] opacity-75">
              {sfOn ? 'Active · click positions' : 'Disabled'}
            </span>
          </span>
          <span
            className={`ml-1 size-2 shrink-0 rounded-full ${
              sfOn
                ? 'bg-positive shadow-[0_0_0_3px_rgba(46,230,107,0.18)]'
                : 'bg-[color:rgba(255,255,255,0.25)]'
            }`}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
};

export default PortfolioActionsBar;
